import React, { useCallback, useEffect } from 'react';
import styles from 'feature/orders/index.module.scss';
import PageLayout from 'layout/page';
import Card from 'components/card';
import { useOrdersContext, APPEND_ORDERS, PAGE_SIZE } from 'feature/orders/use-orders';
import classname from 'classnames/bind';
import LoadingDialog from 'components/loading-dialog';
import { useHistory } from 'react-router-dom';
import { InfiniteLoader, List, AutoSizer } from 'react-virtualized';
import repo from 'service/repository';
import { PATH } from 'config';
import dayjs from 'dayjs';
import appLoader from 'distributor/app-loader';
import { amountFormat } from 'helpers/currency-helper';
import EmptyState from 'components/empty-state';
import amplitude from 'service/amplitude';
import Info from 'components/info';
import { isNpcpay } from 'config';

const cx = classname.bind(styles);

const OrderPage = () => {
  const { state, dispatch, fetchOrders } = useOrdersContext();
  const { configData } = appLoader;
  const { ordersList } = configData;
  const history = useHistory();

  useEffect(() => {
    fetchOrders(ordersList[0].key);
  }, [fetchOrders, ordersList]);

  const onChangeOrderType = useCallback(
    orderType => {
      fetchOrders(orderType);
    },
    [fetchOrders]
  );

  const OrderItem = ({ order }) => {
    const amountClass = cx({
      amount: true,
      [order.paymentState]: true,
    });

    const Amount = () => {
      if (order.paymentState === 'unpaid') {
        return <span className={amountClass}>${amountFormat(order.amount)}・交易失敗</span>;
      } else if (order.paymentState === 'pending') {
        return <span className={amountClass}>${amountFormat(order.amount)}・交易等待中</span>;
      } else {
        return <span className={styles.amount}>${amountFormat(order.amount)}</span>;
      }
    };

    return (
      <Card>
        <div
          className={styles.item}
          onClick={() => {
            history.push({
              pathname: PATH.ORDER_DETAIL,
              search: `?url=${order.url}`,
              state: { plateNumber: order.plateNumber, poi: order.storeName },
            });
          }}
        >
          <div className={styles.left}>
            <span className={styles.first}>{order.storeName}</span>
            <Amount />
          </div>
          <div className={styles.right}>
            <span>{dayjs(order.createdAt).format('YYYY.MM.DD')}</span>
            <span>{order.plateNumber}</span>
          </div>
        </div>
      </Card>
    );
  };

  const isRowLoaded = ({ index }) => {
    return !!state.orders[index];
  };

  const loadMoreRows = useCallback(() => {
    return repo.fetchOrders(state.paging.currentPage + 1, PAGE_SIZE, [state.orderType]).then(data => {
      dispatch({
        type: APPEND_ORDERS,
        payload: data,
      });
    });
  }, [dispatch, state]);

  function rowRenderer({ key, index, style }) {
    const order = state.orders[index];
    return (
      <div key={key} style={{ ...style, padding: '6px 24px' }}>
        <OrderItem order={order} />
      </div>
    );
  }

  const rowCount = useCallback(() => {
    const { currentPage, totalPage, perPage } = state.paging;
    const orderSize = state.orders.length;
    if (currentPage === totalPage) {
      return orderSize;
    } else {
      return perPage * totalPage;
    }
  }, [state]);

  const Orders = () => {
    if (state.orders.length === 0)
      return <EmptyState text={`${ordersList.find(x => state.orderType === x.key).value}訂單`} />;

    return (
      <div className={styles['order-wrapper']}>
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={loadMoreRows}
          rowCount={rowCount()}
          minimumBatchSize={PAGE_SIZE}
          threshold={5}
        >
          {({ onRowsRendered, registerChild }) => {
            return (
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    ref={registerChild}
                    height={height}
                    onRowsRendered={onRowsRendered}
                    rowCount={state.orders.length}
                    rowHeight={107}
                    rowRenderer={rowRenderer}
                    width={width}
                    style={{ paddingBottom: 40 }}
                  />
                )}
              </AutoSizer>
            );
          }}
        </InfiniteLoader>
      </div>
    );
  };

  const Tabs = () => {
    return (
      <div className={styles['tab-layout']}>
        {ordersList.map(x => {
          const tabClasses = cx({
            tab: true,
            active: x.key === state.orderType,
          });
          return (
            <span
              onClick={() => {
                amplitude.orders_tab_onClick({ transactionType: x.value });
                onChangeOrderType(x.key);
              }}
              key={x.key}
              className={tabClasses}
            >
              {x.value}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageLayout noCopyright fullHeight>
        {isNpcpay && (
          <Info theme="blue" customStyle={{ margin: '-30px -24px 20px' }}>
            此頁顯示金額為訂單金額（加油幣抵用及刷卡金額合計），實際刷卡金額請見全國 App 消費哩程頁。
          </Info>
        )}
        <Tabs />
        <Orders />
      </PageLayout>
      <LoadingDialog isShow={state.loading} />
    </>
  );
};

export default OrderPage;
